/**
 * EDD Engine
 * Single source of truth for resolving Estimated Delivery Date messages
 * against the new per-country/per-city payload:
 *   GET {window.eddApiUrl}
 *
 * Exposes window.EddEngine:
 *   - getPayload()                    -> Promise<Array> (fetched/cached payload)
 *   - resolveMessage({ countryCode, cityName, isOutOfStock, locale }) -> Promise<{
 *       text, group, raw, matchedCountry, matchedCity
 *     } | null>
 *
 * Fallback rule: if the detected country_code has no match in the payload,
 * resolution falls back to matching against "AE".
 *
 * Debugging: set window.eddDebug = false before this script loads to silence
 * the [EDD Engine] console logs (on by default).
 */
(function () {
  var CACHE_KEY = 'edd_payload_cache_v2';
  var CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour minimum, per spec

  var payloadPromise = null;

  function debugEnabled() {
    return window.eddDebug !== false;
  }

  function log() {
    if (!debugEnabled()) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[EDD Engine]'].concat(args));
  }

  function warn() {
    if (!debugEnabled()) return;
    var args = Array.prototype.slice.call(arguments);
    console.warn.apply(console, ['[EDD Engine]'].concat(args));
  }

  function getApiUrl() {
    return window.eddApiUrl || 'https://dev-cms.mawsim.co/api/edd/messages/new';
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || !cached.timestamp || !Array.isArray(cached.data)) return null;
      if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
        log('Cache expired (age ' + Math.round((Date.now() - cached.timestamp) / 60000) + 'min), refetching.');
        return null;
      }
      log('Using cached payload (' + cached.data.length + ' countries, age ' + Math.round((Date.now() - cached.timestamp) / 60000) + 'min).');
      return cached.data;
    } catch (e) {
      warn('Failed to read cache:', e);
      return null;
    }
  }

  function writeCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data: data }));
    } catch (e) {
      warn('Failed to write cache (quota/private mode?):', e);
    }
  }

  function fetchPayload() {
    var cached = readCache();
    if (cached) {
      return Promise.resolve(cached);
    }

    var apiUrl = getApiUrl();
    log('Fetching payload from', apiUrl);

    return fetch(apiUrl)
      .then(function (res) {
        log('Fetch response status:', res.status, res.ok ? '(ok)' : '(NOT ok)');
        if (!res.ok) throw new Error('HTTP error! Status: ' + res.status);
        return res.json();
      })
      .then(function (result) {
        var data = (result && Array.isArray(result.data)) ? result.data : [];
        if (data.length === 0) {
          warn('Payload fetched but contains 0 countries. Raw response:', result);
        } else {
          log('Payload fetched OK:', data.length, 'countries ->', data.map(function (c) { return c.country_code; }));
        }
        writeCache(data);
        document.dispatchEvent(new CustomEvent('edd:payload-updated'));
        return data;
      })
      .catch(function (e) {
        warn('Failed to fetch payload (network/CORS/parse error) - EDD messages will be unavailable until next successful fetch:', e);
        return [];
      });
  }

  function getPayload() {
    if (!payloadPromise) {
      payloadPromise = fetchPayload();
    }
    return payloadPromise;
  }

  function normalize(str) {
    return (str || '').toString().trim().toLowerCase();
  }

  function matchCountry(payload, countryCode) {
    var code = normalize(countryCode);
    var found = payload.find(function (c) {
      return normalize(c.country_code) === code;
    });
    if (found) {
      log('Country matched directly:', found.country_code, '(requested "' + countryCode + '")');
      return found;
    }

    // Fallback rule: unmatched country_code defaults to UAE ("AE")
    var fallback = payload.find(function (c) {
      return normalize(c.country_code) === 'ae';
    }) || null;

    if (fallback) {
      warn('No payload entry for country_code "' + countryCode + '" - falling back to AE.');
    } else {
      warn('No payload entry for country_code "' + countryCode + '" AND no AE entry exists in the payload - cannot resolve any message.');
    }
    return fallback;
  }

  function matchCity(country, cityName, locale) {
    if (!country || !cityName || !Array.isArray(country.cities)) return null;
    var target = normalize(cityName);
    var primaryLang = locale === 'ar' ? 'ar' : 'en';
    var secondaryLang = primaryLang === 'ar' ? 'en' : 'ar';

    var byPrimary = country.cities.find(function (city) {
      return city.name && normalize(city.name[primaryLang]) === target;
    });
    if (byPrimary) {
      log('City matched (' + primaryLang + '):', cityName, '->', byPrimary.name);
      return byPrimary;
    }

    var bySecondary = country.cities.find(function (city) {
      return city.name && normalize(city.name[secondaryLang]) === target;
    });
    if (bySecondary) {
      log('City matched (' + secondaryLang + ' fallback):', cityName, '->', bySecondary.name);
      return bySecondary;
    }

    log('No city match for "' + cityName + '" in ' + country.country_code + ' - using country-level edd.');
    return null;
  }

  function getEffectiveEdd(matchedCountry, matchedCity) {
    if (matchedCity && matchedCity.edd) {
      log('Using CITY-level edd from', matchedCity.name);
      return matchedCity.edd;
    }
    if (matchedCity && !matchedCity.edd) {
      log('Matched city', matchedCity.name, 'has edd: null - falling back to country-level edd.');
    }
    if (matchedCountry && !matchedCountry.edd) {
      warn('Matched country', matchedCountry.country_code, 'has edd: null - no EDD data available for this country on the API. This is a CMS data gap, not a theme bug.');
    }
    return matchedCountry ? matchedCountry.edd : null;
  }

  // Last-resort tier: if neither the matched country nor its AE fallback has
  // any edd data, borrow data from whichever payload entry does have some,
  // so a visitor still sees *some* EDD message instead of none at all.
  function getAnyCountryWithData(payload) {
    return payload.find(function (c) { return !!c.edd; }) || null;
  }

  function getNowInTimezone(timeZone) {
    var now = new Date();
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    }).formatToParts(now);

    var dayName = '';
    var hour = 0;
    var minute = 0;

    parts.forEach(function (part) {
      if (part.type === 'weekday') dayName = part.value.toLowerCase();
      if (part.type === 'hour') hour = parseInt(part.value, 10) % 24;
      if (part.type === 'minute') minute = parseInt(part.value, 10);
    });

    return { dayName: dayName, hour: hour, minute: minute, minutesOfDay: hour * 60 + minute };
  }

  function getDateStringInTimezone(timeZone) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone }).format(new Date());
  }

  function parseTimeToMinutes(timeStr) {
    if (!timeStr) return null;
    var parts = timeStr.split(':');
    var h = parseInt(parts[0], 10) || 0;
    var m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  }

  function isWithinDateRange(dateStr, startDate, endDate) {
    if (!startDate || !endDate) return false;
    return dateStr >= startDate && dateStr <= endDate;
  }

  function formatRemainingTime(minutesRemaining, locale) {
    if (minutesRemaining <= 0) return '';
    var isAr = locale === 'ar';
    var h = Math.floor(minutesRemaining / 60);
    var m = minutesRemaining % 60;

    if (isAr) {
      var hStr = h > 0 ? (h + ' ' + (h === 1 ? 'ساعة' : h === 2 ? 'ساعتين' : h <= 10 ? 'ساعات' : 'ساعة')) : '';
      var mStr = m > 0 ? (m + ' ' + (m === 1 ? 'دقيقة' : m === 2 ? 'دقيقتين' : m <= 10 ? 'دقائق' : 'دقيقة')) : '';
      return [hStr, mStr].filter(Boolean).join(' و ');
    }

    var hStrEn = h > 0 ? (h + 'h') : '';
    var mStrEn = m > 0 ? (m + 'm') : '';
    return [hStrEn, mStrEn].filter(Boolean).join(' & ');
  }

  function substitutePlaceholders(text, ctx) {
    if (!text) return text;
    var result = text;
    if (result.indexOf('[TIMER]') !== -1) {
      result = result.split('[TIMER]').join(ctx.timerStr || '');
    }
    if (result.indexOf('[DATES]') !== -1) {
      result = result.split('[DATES]').join(ctx.datesStr || '');
    }
    return result;
  }

  function pickLocalizedText(obj, locale) {
    if (!obj) return '';
    var lang = locale === 'ar' ? 'ar' : 'en';
    return obj[lang] || obj.en || obj.ar || '';
  }

  // Wraps duration mentions (e.g. "8 ساعات و 7 دقيقة", "3 hours") in a span so
  // the frontend can highlight them. Kept as a separate `highlightedText`
  // field (see resolveMessageInternal) rather than mutating `text` itself,
  // since some consumers (e.g. cart-edd-grouping.js) render `resolved.text`
  // via textContent and would otherwise show the raw markup as literal text.
  function highlightDurations(text, locale, className) {
    if (!text) return text;
    var cssClass = className || 'edd-highlight';
    var isAr = locale === 'ar';
    var arUnits = 'ساعة|ساعتين|ساعات|دقيقة|دقيقتين|دقائق';
    // Numbers in Arabic CMS copy may be typed as plain ASCII digits (48) or
    // as Arabic-Indic/Eastern Arabic-Indic numerals (٤٨), so match both.
    var arDigits = '[0-9\\u0660-\\u0669\\u06F0-\\u06F9]+';
    var pattern = isAr
      ? new RegExp(arDigits + '\\s*(?:' + arUnits + ')(?:\\s*و\\s*' + arDigits + '\\s*(?:' + arUnits + '))?', 'g')
      : /\d+\s*(?:hours?|hrs?|h)\b(?:\s*&\s*\d+\s*(?:minutes?|mins?|m)\b)?|\d+\s*(?:minutes?|mins?|m)\b/gi;

    return text.replace(pattern, function (match) {
      return '<span class="' + cssClass + '">' + match + '</span>';
    });
  }

  function resolveMessageInternal(payload, options) {
    var countryCode = options.countryCode;
    var cityName = options.cityName;
    var isOutOfStock = options.isOutOfStock;
    var locale = options.locale;

    if (!Array.isArray(payload) || payload.length === 0) {
      warn('Cannot resolve: payload is empty (fetch may have failed - see warnings above).');
      return null;
    }

    var matchedCountry = matchCountry(payload, countryCode);
    if (!matchedCountry) return null;

    // True when the visitor's own country_code had no payload entry at all
    // and matchCountry() fell back to AE - i.e. matchedCountry is NOT the
    // visitor's real country, just a stand-in used purely to source edd data.
    var usedCountryFallback = normalize(matchedCountry.country_code) !== normalize(countryCode);
    if (usedCountryFallback) {
      log('Matched country "' + matchedCountry.country_code + '" is a fallback stand-in for unmatched country_code "' + countryCode + '" - it is not the visitor\'s real location.');
    }

    var matchedCity = matchCity(matchedCountry, cityName, locale);
    var effectiveEdd = getEffectiveEdd(matchedCountry, matchedCity);

    var timezoneSourceCountry = matchedCountry;
    var usedFallbackCountry = false;

    if (!effectiveEdd) {
      var fallbackDataCountry = getAnyCountryWithData(payload);
      if (fallbackDataCountry) {
        warn('No edd data for matched country "' + matchedCountry.country_code + '" (or its AE fallback) - borrowing edd data from "' + fallbackDataCountry.country_code + '" as a last resort so a message still renders.');
        effectiveEdd = fallbackDataCountry.edd;
        timezoneSourceCountry = fallbackDataCountry;
        matchedCity = null;
        usedFallbackCountry = true;
      } else {
        warn('Cannot resolve: no edd data (country=' + matchedCountry.country_code + ', city=' + (matchedCity ? matchedCity.name.en : 'n/a') + ') AND no country in the entire payload has any edd data.');
        return null;
      }
    }

    var timeZone = timezoneSourceCountry.timezone;
    var nowInfo = getNowInTimezone(timeZone);
    var todayStr = getDateStringInTimezone(timeZone);
    log('Resolved "now" in', timeZone, '->', nowInfo.dayName, nowInfo.hour + ':' + nowInfo.minute, '(date ' + todayStr + ')');

    var messages = null;
    var usedCutoff = false;

    var special = effectiveEdd.special;
    if (special && special.enabled && isWithinDateRange(todayStr, special.start_date, special.end_date)) {
      log('Special window active (' + special.start_date + ' -> ' + special.end_date + '). isOutOfStock=' + isOutOfStock);
      messages = isOutOfStock ? special.messages.oos : special.messages.in_stock;
    } else {
      var dayConfig = effectiveEdd.normal ? effectiveEdd.normal[nowInfo.dayName] : null;
      if (!dayConfig) {
        warn('Cannot resolve: no normal-day config for "' + nowInfo.dayName + '".');
        return null;
      }

      var endTimeEffective = dayConfig.has_cutoff_time ? (dayConfig.end_time || '21:00') : null;
      var startMinutes = parseTimeToMinutes(dayConfig.start_time);
      var endMinutes = parseTimeToMinutes(endTimeEffective);

      var cutoffActive = (
        dayConfig.has_cutoff_time &&
        dayConfig.messages &&
        dayConfig.messages.cutoff &&
        startMinutes !== null &&
        endMinutes !== null &&
        nowInfo.minutesOfDay >= startMinutes &&
        nowInfo.minutesOfDay <= endMinutes
      );

      log('Day config for', nowInfo.dayName, '- has_cutoff_time:', dayConfig.has_cutoff_time, 'cutoffActive:', cutoffActive);

      if (cutoffActive) {
        messages = dayConfig.messages.cutoff;
        usedCutoff = true;

        var minutesRemaining = endMinutes - nowInfo.minutesOfDay;
        var timerStr = formatRemainingTime(minutesRemaining, locale);
        var cutoffText = substitutePlaceholders(pickLocalizedText(messages, locale), { timerStr: timerStr });
        var cutoffGroupText = pickLocalizedText(messages.group, locale);

        log('Resolved CUTOFF message:', cutoffText);

        return {
          text: cutoffText,
          highlightedText: highlightDurations(cutoffText, locale),
          group: cutoffGroupText,
          // Any duration mentioned in the short "group" message (e.g. "3
          // ساعات", "٤٨ ساعة") is highlighted green, same as in_stock/oos
          // group messages below.
          highlightedGroup: highlightDurations(cutoffGroupText, locale, 'edd-highlight-success'),
          raw: messages,
          matchedCountry: matchedCountry,
          matchedCity: matchedCity,
          usedCutoff: usedCutoff,
          usedFallbackCountry: usedFallbackCountry,
          usedCountryFallback: usedCountryFallback
        };
      }

      messages = isOutOfStock ? dayConfig.messages.oos : dayConfig.messages.in_stock;
    }

    if (!messages) {
      warn('Cannot resolve: no', (isOutOfStock ? 'oos' : 'in_stock'), 'message available for this day/special block.');
      return null;
    }

    var datesStr = (special && special.end_date) ? special.end_date : '';
    var finalText = substitutePlaceholders(pickLocalizedText(messages, locale), { datesStr: datesStr });

    var finalGroupText = pickLocalizedText(messages.group, locale);

    log('Resolved message:', finalText, '| group:', finalGroupText);

    return {
      text: finalText,
      highlightedText: highlightDurations(finalText, locale),
      group: finalGroupText,
      highlightedGroup: highlightDurations(finalGroupText, locale, 'edd-highlight-success'),
      raw: messages,
      matchedCountry: matchedCountry,
      matchedCity: matchedCity,
      usedCutoff: usedCutoff,
      usedFallbackCountry: usedFallbackCountry,
      usedCountryFallback: usedCountryFallback
    };
  }

  function resolveMessage(options) {
    options = options || {};
    var normalizedOptions = {
      countryCode: options.countryCode,
      cityName: options.cityName,
      isOutOfStock: !!options.isOutOfStock,
      locale: options.locale === 'ar' ? 'ar' : 'en'
    };

    log('resolveMessage() called with', normalizedOptions);

    return getPayload().then(function (payload) {
      try {
        return resolveMessageInternal(payload, normalizedOptions);
      } catch (e) {
        console.error('[EDD Engine] Unexpected error while resolving message:', e, 'options:', normalizedOptions);
        return null;
      }
    });
  }

  window.EddEngine = {
    getPayload: getPayload,
    resolveMessage: resolveMessage
  };

  log('EddEngine ready. API URL:', getApiUrl());
})();
